﻿<template>
    <div class="pwa-toast">
        <q-banner
            v-if="offlineReady || needRefresh"
            :class="bannerClass"
            class="shadow-3 rounded-borders q-pa-md"
            rounded
        >
            <template #avatar>
                <q-icon :name="bannerIcon" size="1.6em" />
            </template>

            <div class="text-body2 text-weight-medium">
                <span v-if="offlineReady">
                    {{ ts('offlineReady') }}
                </span>
                <span v-else>
                    {{ ts('newVersion') }}
                </span>
            </div>

            <template #action>
                <q-btn
                    v-if="needRefresh"
                    dense
                    flat
                    :label="ts('reload')"
                    icon="refresh"
                    :text-color="buttonTextColor"
                    @click="reloadPage"
                />
                <q-btn
                    dense
                    flat
                    :label="ts('close')"
                    icon="close"
                    :text-color="buttonTextColor"
                    @click="close"
                />
            </template>
        </q-banner>
    </div>
</template>

<script setup lang="ts">
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { ts } from '@/plugins/i18nFormat'
import { computed, onBeforeUnmount } from 'vue'

const intervalMS = 60 * 60 * 1000

let updateInterval: ReturnType<typeof setInterval> | undefined

const {
    offlineReady,
    needRefresh,
    updateServiceWorker,
} = useRegisterSW({
    onOfflineReady() {
        console.log('Offline ready')
    },
    onRegisteredSW(swUrl, r) {
        if (!r)
            return

        if (updateInterval)
            clearInterval(updateInterval)

        updateInterval = setInterval(async () => {
            if (r.installing || !navigator)
                return

            if (('connection' in navigator) && !navigator.onLine)
                return

            const resp = await fetch(swUrl, {
                cache: 'no-store',
                headers: {
                    'cache': 'no-store',
                    'cache-control': 'no-cache',
                },
            })

            if (resp?.status === 200)
                await r.update()
        }, intervalMS)
    },
})

const bannerClass = computed(() => (
    needRefresh.value
        ? 'bg-primary text-white'
        : 'bg-grey-2 text-dark'
))

const bannerIcon = computed(() => (
    needRefresh.value
        ? 'system_update_alt'
        : 'cloud_done'
))

const buttonTextColor = computed(() => (
    needRefresh.value
        ? 'white'
        : 'dark'
))

async function close() {
    offlineReady.value = false
    needRefresh.value = false
}

async function reloadPage() {
    await updateServiceWorker(true)
}

onBeforeUnmount(() => {
    if (updateInterval)
        clearInterval(updateInterval)
})
</script>

<style scoped lang="sass">
.pwa-toast
    position: fixed
    right: 16px
    bottom: 16px
    z-index: 2000
    width: min(420px, calc(100vw - 32px))
</style>
